/**
 * Deterministic Smart Report PDF Engine.
 *
 * Design goals (per product spec):
 *  - Every laboratory can have a different header/footer height.
 *  - Every printed page contains: laboratory header (top), safe padding, content, safe padding, laboratory footer (bottom).
 *  - Content NEVER overlaps header, footer, or their 10 mm safe padding.
 *  - Cards (biomarker cards, follow-up cards, charts, tables, disclaimer, insight cards…) MUST NOT split across pages.
 *  - Section titles keep-with-next so they never orphan at the bottom of a page.
 *  - Support A4 / Letter / Legal / arbitrary custom sizes without hard-coding dimensions.
 *  - Zero dependency on browser flow pagination — the engine bin-packs blocks into fixed-size .pdf-page
 *    divs, and the print stylesheet forces one .pdf-page = one paper page.
 *
 * Two-phase render:
 *   1. Blocks are mounted in a hidden `measurer` at the exact content-width so we get accurate rendered heights.
 *   2. A pure JS pagination pass assigns each block to a page; the visible tree renders a stack of PrintPage
 *      elements, each an absolutely-sized paper sheet.
 */

import { useLayoutEffect, useMemo, useRef, useState, forwardRef } from "react";
import { Sparkles, ChevronRight, ShieldAlert } from "lucide-react";
import { Card } from "@/components/ui/card";
import {
  PatientMetaCard,
  LifestyleTab,
  PrintRunningHeader,
  PrintRunningFooter,
  getLetterhead,
  TrendChart,
} from "../SmartReport";
import { statusColor, parseRange, rangePosition } from "@/lib/health";

/* ─────────────────────────── constants ─────────────────────────── */
const PAGE_SIZES = {
  A4: { w: 210, h: 297 },
  Letter: { w: 216, h: 279 },
  Legal: { w: 216, h: 356 },
};
const MM_PER_INCH = 25.4;
const PX_PER_INCH = 96;
const MM_TO_PX = PX_PER_INCH / MM_PER_INCH; // ≈3.7795
const SAFE_TOP_MM = 10;
const SAFE_BOTTOM_MM = 10;
const BLOCK_GAP_MM = 3;

const mmToPx = (mm) => mm * MM_TO_PX;
const pxToMm = (px) => px / MM_TO_PX;

/* ─────────────────────────── PUBLIC ENTRY POINT ─────────────────────────── */
/**
 * <PrintDocument> — expose via `ref` (used by react-to-print). Renders a stack of paginated pages.
 * Off-print-media, this component is positioned off-screen so it doesn't affect the on-screen report UI.
 */
export const PrintDocument = forwardRef(function PrintDocument(
  { report, lab, smart, healthScore, band, history = [] },
  ref
) {
  const lh = useMemo(() => getLetterhead(lab) || defaultLh(), [lab]);
  const page = PAGE_SIZES[lh.page_size] || PAGE_SIZES.A4;
  const headerH = Math.max(0, Number(lh.header_height_mm) || 0);
  const footerH = Math.max(0, Number(lh.footer_height_mm) || 0);
  const marginL = Number(lh.page_margin_left_mm) || 0;
  const marginR = Number(lh.page_margin_right_mm) || 0;

  // Usable content area PER PAGE
  const contentW_mm = page.w - marginL - marginR;
  const contentH_mm = page.h - headerH - footerH - SAFE_TOP_MM - SAFE_BOTTOM_MM;
  const contentW_px = mmToPx(contentW_mm);

  /* ─────── Enumerate atomic renderable blocks ─────── */
  const blocks = useMemo(
    () => buildBlocks({ report, lab, smart, healthScore, band, history }),
    [report, lab, smart, healthScore, band, history]
  );

  /* ─────── Phase 1: measure each block ─────── */
  const measurerRef = useRef(null);
  const [heights, setHeights] = useState(/** @type {Record<string,number>} */ ({}));

  useLayoutEffect(() => {
    const root = measurerRef.current;
    if (!root) return;
    // Wait one frame so fonts/images/charts finish initial layout.
    const raf = requestAnimationFrame(() => {
      const next = {};
      root.querySelectorAll("[data-block-key]").forEach((node) => {
        const k = node.getAttribute("data-block-key");
        const h = node.getBoundingClientRect().height;
        next[k] = pxToMm(h);
      });
      setHeights(next);
    });
    return () => cancelAnimationFrame(raf);
  }, [blocks, contentW_px]);

  /* ─────── Phase 2: pagination ─────── */
  const pages = useMemo(
    () => paginate(blocks, heights, contentH_mm),
    [blocks, heights, contentH_mm]
  );

  /* ─────── Render ─────── */
  return (
    <div ref={ref} data-testid="print-document" className="pdf-doc">
      {/* Print stylesheet — scoped to this document */}
      <style>{`
        /* On-screen: keep the document off-screen so it doesn't affect the interactive UI */
        .pdf-doc {
          position: absolute;
          left: -100000px; top: 0;
          width: ${page.w}mm;
          background: #fff;
          /* Comfortable baseline line-height so descenders (y/g/p/j/q) aren't clipped
             by overflow-hidden containers in Chromium's PDF renderer. */
          line-height: 1.5;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          text-rendering: geometricPrecision;
        }
        /* Give truncated one-liners breathing room so descenders don't get clipped
           by \`.truncate\` (overflow:hidden + white-space:nowrap). */
        .pdf-doc .truncate {
          line-height: 1.55 !important;
          padding-bottom: 2px !important;
          padding-top: 1px !important;
        }
        /* Same protection for short text lines that sit tightly against card borders. */
        .pdf-doc .text-\\[9px\\], .pdf-doc .text-\\[10px\\], .pdf-doc .text-\\[10\\.5px\\],
        .pdf-doc .text-\\[11px\\], .pdf-doc .text-\\[12px\\], .pdf-doc .text-\\[13px\\],
        .pdf-doc .text-xs, .pdf-doc .text-sm {
          line-height: 1.45;
        }
        /* Recharts occasionally paints ticks/legend text above the plot area.
           Isolate chart containers so their overflow can't bleed into the
           preceding title/label row. */
        .pdf-doc .recharts-responsive-container,
        .pdf-doc .recharts-wrapper,
        .pdf-doc .recharts-surface {
          overflow: hidden !important;
        }
        .pdf-doc .pdf-page {
          width: ${page.w}mm;
          height: ${page.h}mm;
          position: relative;
          overflow: hidden;
          background: #fff;
          box-sizing: border-box;
        }
        .pdf-doc .pdf-measurer {
          position: absolute;
          left: -99999px; top: 0;
          width: ${contentW_px}px;
          visibility: hidden;
          pointer-events: none;
        }
        @media print {
          @page {
            size: ${page.w}mm ${page.h}mm;
            margin: 0 !important;
          }
          html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
          /* The doc root becomes normal-flow inside the print iframe */
          .pdf-doc {
            position: static !important;
            left: 0 !important; top: 0 !important;
          }
          .pdf-doc .pdf-measurer { display: none !important; }
          .pdf-doc .pdf-page {
            page-break-after: always !important;
            break-after: page !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          .pdf-doc .pdf-page:last-child {
            page-break-after: auto !important;
            break-after: auto !important;
          }
        }
      `}</style>

      {/* Hidden measurer — mounts each block at exact content-width in px so heights are accurate */}
      <div ref={measurerRef} className="pdf-measurer" aria-hidden="true">
        {blocks.map((b) => (
          <div key={b.key} data-block-key={b.key} style={{ marginBottom: `${BLOCK_GAP_MM}mm` }}>
            {b.node}
          </div>
        ))}
      </div>

      {/* Final paginated output */}
      {pages.map((pageBlocks, i) => (
        <PrintPage
          key={i}
          number={i + 1}
          total={pages.length}
          page={page}
          lh={lh}
          lab={lab}
          report={report}
          headerH={headerH}
          footerH={footerH}
          marginL={marginL}
          marginR={marginR}
        >
          {pageBlocks.map((b) => (
            <div key={b.key} style={{ marginBottom: `${BLOCK_GAP_MM}mm` }}>
              {b.node}
            </div>
          ))}
        </PrintPage>
      ))}
    </div>
  );
});

/* ─────────────────────────── PrintPage ─────────────────────────── */
function PrintPage({
  number,
  total,
  page,
  lh: _lh, // reserved for future per-page overrides
  lab,
  report,
  headerH,
  footerH,
  marginL,
  marginR,
  children,
}) {
  const contentTop = headerH + SAFE_TOP_MM;
  const contentBottom = footerH + SAFE_BOTTOM_MM;
  return (
    <div className="pdf-page" data-testid={`pdf-page-${number}`}>
      {/* Header — glued to absolute top of every page */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: `${headerH}mm`,
          overflow: "hidden",
        }}
        data-testid="pdf-page-header"
      >
        <PrintRunningHeader lab={lab} report={report} />
      </div>

      {/* Content area — bounded by safe padding on top and bottom */}
      <div
        style={{
          position: "absolute",
          top: `${contentTop}mm`,
          bottom: `${contentBottom}mm`,
          left: `${marginL}mm`,
          right: `${marginR}mm`,
          overflow: "hidden",
        }}
        data-testid="pdf-page-content"
      >
        {children}
      </div>

      {/* Footer — glued to absolute bottom of every page */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: `${footerH}mm`,
          overflow: "hidden",
        }}
        data-testid="pdf-page-footer"
      >
        <PrintRunningFooter lab={lab} report={report} />
      </div>

      {/* Page number — sits inside the bottom safe padding so it never overlaps footer artwork */}
      <div
        style={{
          position: "absolute",
          bottom: `${footerH + 2}mm`,
          right: `${marginR + 3}mm`,
          fontSize: "9px",
          letterSpacing: "0.04em",
          color: "#94A3B8",
          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        }}
        data-testid="pdf-page-number"
      >
        Page {number} of {total}
      </div>
    </div>
  );
}

/* ─────────────────────────── PAGINATION ─────────────────────────── */
/**
 * Bin-pack blocks into pages of usable height `contentH_mm`.
 * Rules:
 *   • A block is atomic — never split.
 *   • If a block is larger than contentH_mm, it renders alone on its own page (best-effort; caller should
 *     make sure such blocks don't exist; e.g. by pre-splitting long lists into per-item blocks).
 *   • Blocks with `keepWithNext=true` must fit on the same page as the following block, else both spill.
 */
function paginate(blocks, heights, contentH_mm) {
  if (!blocks.length) return [];
  // If we haven't measured yet, put everything on one page so we can measure it — actual pagination happens on the next render.
  const measured = Object.keys(heights).length >= blocks.length;
  if (!measured) return [blocks];

  const pages = [];
  let cur = [];
  let used = 0;

  const push = (b, h) => {
    if (cur.length) used += BLOCK_GAP_MM;
    cur.push(b);
    used += h;
  };
  const flush = () => {
    if (cur.length) {
      pages.push(cur);
      cur = [];
      used = 0;
    }
  };

  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i];
    const h = heights[b.key] ?? 0;

    // Larger than a whole page → give it its own page (best effort).
    if (h > contentH_mm) {
      flush();
      pages.push([b]);
      continue;
    }

    // If block has keepWithNext, require space for BOTH this and the following block —
    // UNLESS the following block is itself oversized (bigger than one page). In that case,
    // it will go on its own page regardless, so the section title would be orphaned; better to
    // just place the title where it fits and let the oversized block flow to its own page.
    let requiredExtra = 0;
    if (b.keepWithNext && i + 1 < blocks.length) {
      const nextH = heights[blocks[i + 1].key] ?? 0;
      if (nextH > 0 && nextH <= contentH_mm) {
        requiredExtra = BLOCK_GAP_MM + nextH;
      }
    }
    const need = (cur.length ? BLOCK_GAP_MM : 0) + h + requiredExtra;
    if (used + need > contentH_mm) {
      flush();
    }
    push(b, h);
  }
  flush();
  return pages;
}

/* ─────────────────────────── BLOCK BUILDER ─────────────────────────── */
/**
 * Enumerate the report into an ordered array of atomic blocks. Each block:
 *   { key: unique string, node: React element, keepWithNext?: bool }
 */
function buildBlocks({ report, lab: _lab, smart, healthScore, band, history }) {
  const bs = [];
  if (!smart) return bs;

  // 1. Patient meta — first block, always page 1.
  bs.push({
    key: "patient-meta",
    node: (
      <PatientMetaCard
        report={report}
        smart={smart}
        healthScore={healthScore}
        band={band}
        onAskAI={() => {}}
        hideInteractive
      />
    ),
  });

  // 2. Smart Health Summary — split into two atomic blocks so neither exceeds one page.
  bs.push({ key: "sec-summary", keepWithNext: true, node: <SectionTitle title="Smart Health Summary" /> });
  bs.push({ key: "summary-text", node: <SummaryTextCard smart={smart} /> });
  if (smart.health_snapshot?.length > 0) {
    bs.push({ key: "summary-snapshot", node: <HealthSnapshotCard snapshot={smart.health_snapshot} /> });
  }

  // 3. Biomarker Details — group by panel, one atomic card per panel
  const biomarkers = smart.biomarkers || [];
  const panelCommentMap = new Map();
  (smart.panel_comments || []).forEach((pc) => {
    if (pc?.panel && pc?.comment) panelCommentMap.set(pc.panel, pc.comment);
  });
  if (biomarkers.length) {
    const panels = groupByPanel(biomarkers);
    bs.push({ key: "sec-biomarkers", keepWithNext: true, node: <SectionTitle title="Biomarker Details" /> });
    panels.forEach(([panel, items], i) => {
      // Big panels (>10 rows) can exceed one page — chunk them into cards of 10.
      const chunks = chunk(items, 10);
      chunks.forEach((rows, j) => {
        bs.push({
          key: `panel-${i}-${j}`,
          node: (
            <BiomarkerPanelCard
              panel={chunks.length > 1 ? `${panel} (${j + 1}/${chunks.length})` : panel}
              items={rows}
              comment={j === chunks.length - 1 ? panelCommentMap.get(panel) : undefined}
            />
          ),
        });
      });
    });
  }

  // 3b. General notes / observations
  if ((smart.general_notes || []).length) {
    bs.push({ key: "sec-notes", keepWithNext: true, node: <SectionTitle title="Laboratory Notes & Observations" /> });
    bs.push({ key: "notes-block", node: <GeneralNotesBlock notes={smart.general_notes} /> });
  }

  // 4. Critical findings — one card per finding
  const criticals = smart.critical_findings || [];
  if (criticals.length) {
    bs.push({ key: "sec-critical", keepWithNext: true, node: <SectionTitle title="Critical Findings" tone="danger" /> });
    criticals.forEach((c, i) => bs.push({ key: `critical-${i}`, node: <CriticalOneCard c={c} /> }));
  }

  // 5. Trends — one chart per biomarker with history
  if (history.length >= 2) {
    const withHistory = biomarkers
      .filter((b) => history.some((r) => r.biomarkers?.some((x) => x.name === b.name)))
      .slice(0, 8);
    if (withHistory.length) {
      bs.push({ key: "sec-trends", keepWithNext: true, node: <SectionTitle title="Compare to Past" /> });
      withHistory.forEach((b, i) =>
        bs.push({ key: `trend-${i}`, node: <TrendChart biomarker={b} history={history} /> })
      );
    }
  }

  // 6. Lifestyle & Food — one atomic block (3-column grid of small advice cards)
  const hasLifestyle =
    (smart.diet_recommendations?.length || 0) +
      (smart.physical_activity?.length || 0) +
      (smart.avoid_watch_out?.length || 0) >
    0;
  if (hasLifestyle) {
    bs.push({ key: "sec-lifestyle", keepWithNext: true, node: <SectionTitle title="Lifestyle & Food" /> });
    bs.push({ key: "lifestyle", node: <LifestyleTab smart={smart} /> });
  }

  // 7. Follow-up tests — one card per test
  const followups = smart.follow_up_tests || [];
  if (followups.length) {
    bs.push({ key: "sec-followup", keepWithNext: true, node: <SectionTitle title="Book Follow-Up Tests" /> });
    followups.forEach((t, i) =>
      bs.push({ key: `followup-${i}`, node: <FollowUpOneCard t={t} lab={_lab} report={report} /> })
    );
  }

  // 8. Report integrity check — Zero Data Loss transparency.
  bs.push({ key: "integrity", node: <IntegrityBlock smart={smart} /> });

  // 9. Disclaimer
  bs.push({ key: "disclaimer", node: <DisclaimerBlock /> });
  return bs;
}

/* ─────────────────────────── Atomic block components ─────────────────────────── */

function SectionTitle({ title, tone = "default" }) {
  const color = tone === "danger" ? "#B91C1C" : "#111827";
  return (
    <h2
      style={{
        fontFamily: "'Poppins', -apple-system, sans-serif",
        fontWeight: 700,
        fontSize: "15px",
        color,
        margin: "0 0 6px 0",
        padding: "0 0 4px 0",
        borderBottom: "1px solid rgba(17,24,39,0.2)",
      }}
    >
      {title}
    </h2>
  );
}

function SummaryTextCard({ smart }) {
  return (
    <Card className="p-4 rounded-xl" style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
      <div className="flex items-center gap-2 text-efgh-green font-heading font-semibold text-[13px]">
        <Sparkles className="w-3.5 h-3.5" /> Smart Health Summary
      </div>
      <p className="mt-2 text-[11px] text-ink/80 leading-relaxed whitespace-pre-line">{smart.ai_summary}</p>
      <div className="mt-3 grid grid-cols-3 gap-2">
        <MiniStat bg="bg-danger-light" fg="text-danger" val={smart.parameters_needing_attention} label="Need attention" />
        <MiniStat bg="bg-warning-light" fg="text-warning" val={smart.parameters_borderline} label="Borderline" />
        <MiniStat bg="bg-success-light" fg="text-success" val={smart.parameters_normal} label="All clear" />
      </div>
    </Card>
  );
}

function MiniStat({ bg, fg, val, label }) {
  return (
    <div className={`rounded-lg p-2.5 ${bg}`}>
      <div className={`font-heading font-bold text-xl ${fg}`}>{val}</div>
      <div className="text-[10px] text-ink/70">{label}</div>
    </div>
  );
}

const SNAPSHOT_ICON_MAP = {
  Thyroid: "🦋", Liver: "🫁", Kidney: "🫘", "Blood Sugar": "🩸", Heart: "❤️",
  Calcium: "🦴", "Vitamin D": "☀️", "Vitamin B12": "💊", Hemoglobin: "💪", "WBC Count": "🧪",
};

function HealthSnapshotCard({ snapshot }) {
  return (
    <Card className="p-4 rounded-xl" style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
      <div className="font-heading font-semibold text-ink text-[13px]">Health Snapshot</div>
      <p className="text-[10px] text-ink/60 mt-0.5">System-by-system overview</p>
      <div className="mt-3 grid grid-cols-5 gap-2">
        {snapshot.map((h) => (
          <div
            key={h.name}
            className="rounded-lg border border-border text-center"
            style={{
              padding: "8px 4px 10px 4px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: "4px",
              minHeight: "82px",
              overflow: "visible",
            }}
          >
            <div style={{ fontSize: "18px", lineHeight: 1.2, height: "22px" }}>
              {SNAPSHOT_ICON_MAP[h.name] || "🩺"}
            </div>
            <div
              style={{
                fontSize: "10px",
                fontWeight: 500,
                color: "#111827",
                lineHeight: 1.35,
                padding: "0 2px 2px 2px",
                overflow: "visible",
                wordBreak: "keep-all",
              }}
            >
              {h.name}
            </div>
            <span
              className={`pill pill-${h.status}`}
              style={{ fontSize: "9px", lineHeight: 1.3, padding: "2px 8px", marginTop: "auto" }}
            >
              {h.status.replace(/_/g, " ")}
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function BiomarkerPanelCard({ panel, items, comment }) {
  const abnormal = items.filter((x) => x.status !== "normal").length;
  return (
    <Card className="p-4 rounded-xl" style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
      <div className="flex items-center gap-3 mb-2">
        <div className="w-1 h-5 rounded-full" style={{ background: "#F97316" }} />
        <div className="font-heading font-semibold text-ink text-[13px]">{panel}</div>
        <span className="text-[10px] uppercase tracking-wider text-ink/40">{items.length} tests</span>
        {abnormal > 0 && (
          <span className="pill pill-borderline text-[9px]">{abnormal} flagged</span>
        )}
      </div>
      <div className="divide-y divide-border rounded-lg border border-border">
        {items.map((b, i) => (
          <div key={i} className="p-2.5 flex items-start gap-3">
            <span
              className="w-2 h-2 rounded-full shrink-0 mt-1.5"
              style={{ backgroundColor: statusColor(b.status) }}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-medium text-ink text-[12px]">{b.name}</span>
                {b.flag && (
                  <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-danger-light text-danger">{b.flag}</span>
                )}
              </div>
              {b.interpretation && (
                <div className="text-[10px] text-ink/60 mt-0.5">{b.interpretation}</div>
              )}
              {(b.method || b.remarks || b.lab_comment) && (
                <div className="mt-0.5 text-[9px] text-ink/50 leading-tight">
                  {b.method && <div><span className="text-ink/40">Method:</span> {b.method}</div>}
                  {b.remarks && <div><span className="text-ink/40">Remarks:</span> {b.remarks}</div>}
                  {b.lab_comment && <div><span className="text-ink/40">Note:</span> {b.lab_comment}</div>}
                </div>
              )}
            </div>
            <div className="text-right shrink-0">
              <div className="font-heading font-bold text-ink text-[13px]">
                {b.value}
                <span className="text-[10px] text-ink/50 font-normal ml-1">{b.unit}</span>
              </div>
              <div className="text-[9px] text-ink/50">Ref: {b.reference_range}</div>
              {Array.isArray(b.all_reference_ranges) && b.all_reference_ranges.length >= 2 && (
                <div className="text-[8px] text-ink/45 mt-0.5" style={{ lineHeight: 1.35 }}>
                  {b.all_reference_ranges.map((r) => `${r.label}: ${r.display}`).join(" · ")}
                </div>
              )}
            </div>
            <span className={`pill pill-${b.status} text-[9px] mt-0.5`}>
              {String(b.status).toUpperCase()}
            </span>
          </div>
        ))}
      </div>
      {comment && (
        <div className="mt-2 p-2 rounded bg-slate-50 border-l-2 border-emerald-500 text-[10px] text-ink/70 italic">
          <b className="not-italic text-ink/60 mr-1">Laboratory note:</b>{comment}
        </div>
      )}
    </Card>
  );
}

function IntegrityBlock({ smart }) {
  const integrity = smart?.integrity || {};
  const bios = smart?.biomarkers || [];
  const declared = Number(integrity.declared_count ?? smart?.declared_total ?? bios.length);
  const extracted = Number(integrity.extracted_count ?? bios.length);
  const status = integrity.status || (declared === extracted ? "validated" : "unknown");
  const isOk = status === "validated" || status === "recovered";
  const confidence = integrity.confidence ?? (declared === extracted ? 1.0 : 0);
  const pct = Math.round(confidence * 100);
  return (
    <div
      style={{
        pageBreakInside: "avoid",
        breakInside: "avoid",
        padding: "12px 14px",
        borderRadius: "12px",
        border: `1.5px solid ${isOk ? "#A7F3D0" : "#FCD34D"}`,
        background: isOk ? "#ECFDF5" : "#FFFBEB",
        display: "flex",
        alignItems: "flex-start",
        gap: "10px",
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          background: isOk ? "#10B981" : "#F59E0B",
          color: "white",
          display: "grid",
          placeItems: "center",
          fontWeight: 700,
          fontSize: 14,
          flexShrink: 0,
        }}
      >
        {isOk ? "✓" : "!"}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: 12, color: "#111827" }}>
          Report Integrity Check · {String(status).toUpperCase()}
        </div>
        <div style={{ fontSize: 11, color: "#111827", marginTop: 4, fontWeight: 600 }}>
          {isOk
            ? `${extracted} of ${declared || extracted} parameters successfully extracted and displayed.`
            : `${extracted} of ${declared} parameters extracted — extraction incomplete.`}
        </div>
        <div style={{ fontSize: 10, color: "#4B5563", marginTop: 4 }}>
          Source: Original laboratory report · Extraction accuracy: {pct}%
        </div>
      </div>
    </div>
  );
}

function CriticalOneCard({ c }) {
  const range = parseRange(c.reference_range);
  const val = parseFloat(c.value);
  const _pos = rangePosition(val, range);
  return (
    <div
      className="p-4 rounded-xl bg-danger-light/60 border border-danger/20"
      style={{ pageBreakInside: "avoid", breakInside: "avoid" }}
    >
      <div className="grid md:grid-cols-[1fr_auto] gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: statusColor(c.status) }} />
            <div className="font-heading font-semibold text-ink text-[13px]">{c.name}</div>
            <span className={`pill pill-${c.status} text-[9px]`}>{String(c.status).toUpperCase()}</span>
          </div>
          {c.interpretation && (
            <div className="mt-1.5 text-[11px] text-ink/80">
              <b className="text-danger">Impact:</b> {c.interpretation}
            </div>
          )}
          <div className="mt-1.5 text-[11px] text-ink/80">
            <b className="text-efgh-green">How to improve:</b> Consult your doctor, adjust diet &amp;
            lifestyle, retest in 4-6 weeks.
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-heading font-bold text-danger text-lg">
            {c.value} <span className="text-[10px] font-normal text-ink/50">{c.unit}</span>
          </div>
          <div className="text-[10px] text-ink/50">Normal: {c.reference_range}</div>
        </div>
      </div>
    </div>
  );
}

function buildBookingHref(t, lab, report) {
  // Prefer WhatsApp when the lab is configured for it AND we have a phone to pre-fill.
  const waNum = String(lab?.booking_whatsapp_number || lab?.whatsapp_number || "").replace(/[^\d]/g, "");
  const phone = report?.patient_phone || report?.patient_whatsapp || "";
  const patientName = report?.patient_name || "";
  // In a PDF, `window.location.origin` becomes the render server's URL
  // (e.g. http://127.0.0.1:3000) — that would produce dead links for the
  // patient. CRA inlines `process.env.REACT_APP_BACKEND_URL` at build time,
  // so we can access it directly WITHOUT a `typeof process` guard (which
  // otherwise short-circuits false at runtime in the browser and falls back
  // to the render-server origin).
  const envOrigin = process.env.REACT_APP_BACKEND_URL || "";
  const origin =
    envOrigin ||
    (typeof window !== "undefined" ? window.location.origin : "") ||
    "";
  const reportUrl = report?.id && origin ? `${origin}/report/${report.id}` : "";
  if (lab?.booking_mode === "whatsapp" && waNum && phone) {
    const msg = [
      `Hello ${lab?.name || "Lab"},`,
      "",
      "I would like to book the following recommended test:",
      `• ${t.name}`,
      "",
      `Patient Name: ${patientName || "(please ask)"}`,
      `Mobile: ${phone}`,
      report?.id ? `Report ID: ${report.id.slice(0, 8)}` : "",
      "Preferred collection: Home Collection",
      reportUrl ? `Smart Report: ${reportUrl}` : "",
    ].filter(Boolean).join("\n");
    return `https://wa.me/${waNum}?text=${encodeURIComponent(msg)}`;
  }
  // Fallback: EFGH public booking page for the lab, or marketplace when lab slug unavailable.
  const slug = lab?.slug;
  const q = new URLSearchParams();
  q.set("tests", t.name);
  if (report?.id) q.set("reportId", report.id);
  if (patientName) q.set("name", patientName);
  if (phone) q.set("phone", phone);
  if (slug && origin) return `${origin}/l/${slug}/book?${q.toString()}`;
  if (origin) return `${origin}/book-tests?${q.toString()}`;
  return "#";
}

function FollowUpOneCard({ t, lab, report }) {
  const href = buildBookingHref(t, lab, report);
  return (
    <div
      className="p-4 rounded-xl border border-border"
      style={{ pageBreakInside: "avoid", breakInside: "avoid" }}
    >
      <div className="flex items-start gap-2">
        <ShieldAlert className="w-4 h-4 text-efgh-orange shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-heading font-semibold text-ink text-[13px]">{t.name}</div>
          <div className="text-[11px] text-ink/60 mt-1">{t.reason}</div>
          <div className="mt-2 text-[10px] text-efgh-green font-medium">{t.frequency}</div>
        </div>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`pdf-book-now-${(t.name || "").replace(/\s+/g, "-").toLowerCase()}`}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            background: "#0B1628",
            color: "white",
            fontSize: "11px",
            fontWeight: 600,
            padding: "6px 12px",
            borderRadius: "999px",
            textDecoration: "none",
            whiteSpace: "nowrap",
            flexShrink: 0,
            marginTop: "2px",
          }}
        >
          Book Now →
        </a>
      </div>
    </div>
  );
}

function DisclaimerBlock() {
  return (
    <p
      style={{
        fontSize: "10px",
        color: "rgba(17,24,39,0.55)",
        textAlign: "center",
        maxWidth: "160mm",
        margin: "6mm auto 0",
        lineHeight: 1.45,
      }}
    >
      <b>Medical Disclaimer:</b> This report is generated by EFGH&apos;s AI for educational purposes only.
      Always consult a qualified healthcare professional before making any health decisions.
    </p>
  );
}

function GeneralNotesBlock({ notes }) {
  return (
    <div
      style={{
        pageBreakInside: "avoid",
        breakInside: "avoid",
        padding: "10px 12px",
        borderRadius: "10px",
        border: "1px solid rgba(17,24,39,0.1)",
        background: "#F8FAFC",
      }}
    >
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {notes.map((n, i) => (
          <li
            key={i}
            style={{
              fontSize: "10px",
              color: "rgba(17,24,39,0.75)",
              paddingLeft: "10px",
              position: "relative",
              lineHeight: 1.5,
              marginBottom: "3px",
            }}
          >
            <span
              style={{
                position: "absolute",
                left: 0,
                top: "6px",
                width: "3px",
                height: "3px",
                borderRadius: "50%",
                background: "rgba(17,24,39,0.4)",
              }}
            />
            {n}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ─────────────────────────── helpers ─────────────────────────── */

function groupByPanel(list) {
  const map = new Map();
  list.forEach((b) => {
    const key = b.panel || b.category || "Other";
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(b);
  });
  return Array.from(map.entries()).sort(([, a], [, b]) => {
    const abnA = a.filter((x) => x.status !== "normal").length;
    const abnB = b.filter((x) => x.status !== "normal").length;
    if (abnA !== abnB) return abnB - abnA;
    return b.length - a.length;
  });
}

function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

function defaultLh() {
  return {
    page_size: "A4",
    header_height_mm: 24,
    footer_height_mm: 16,
    page_margin_left_mm: 8,
    page_margin_right_mm: 8,
  };
}
